import { createSelector } from 'reselect'

import {
  MEETING_INSIGHTS_BETA_ADDONS,
  MEETING_INTELLIGENCE_ADDONS
} from 'features/billing/modals/plan_selection_modal/config/addons'
import { ENTERPRISE, STANDARD, TEAMS } from 'features/billing/modals/plan_selection_modal/config/tiers'

import { humanizeTier, standardizeSku } from './helpers'
import { CRM_TRIAL_FEATURES } from './hooks/use_feature_list'

// NOTE: Because of scheduled downgrades, order matters.
// Less expensive SKUs should come first.
export const paidSkus = ['essentials', STANDARD, 'professional', TEAMS, ENTERPRISE]

const legacySkus = ['essentials', 'professional']

// These features are restricted either entirely or partially on trial users
const restrictedOnTrialFeatures = [...CRM_TRIAL_FEATURES]

export const featureRootSelector = ({ billing }) => billing

const analyticsEventSelector = createSelector(
  featureRootSelector,
  ({ analyticsSlice }) => analyticsSlice
)
export const analyticsLocationSelector = createSelector(
  analyticsEventSelector,
  ({ location }) => location
)
const availableAddonsSelector = createSelector(
  featureRootSelector,
  ({ availableAddons }) => availableAddons
)
export const currentSubscriptionSelector = createSelector(
  featureRootSelector,
  ({ currentSubscription }) => currentSubscription
)
export const isCurrentSubscriptionLoadedSelector = createSelector(
  currentSubscriptionSelector,
  ({ error, initialized }) => initialized && !error
)
const paymentSelector = createSelector(
  featureRootSelector,
  ({ payment }) => payment
)
const billingInfoSelector = createSelector(
  featureRootSelector,
  ({ billingInfo }) => billingInfo
)
const upgradeSubscriptionSelector = createSelector(
  featureRootSelector,
  ({ upgradeSubscription }) => upgradeSubscription
)
const downgradeSelector = createSelector(
  featureRootSelector,
  ({ downgrade }) => downgrade
)
const contactSupportSelector = createSelector(
  featureRootSelector,
  ({ contactSupport }) => contactSupport
)
export const billingPolicyLoadingSelector = createSelector(
  featureRootSelector,
  ({ policy }) => !policy || !policy.initialized
)
const billingPolicyErrorSelector = createSelector(
  featureRootSelector,
  ({ policy }) => policy && policy.error
)
export const canManageBillingSelector = createSelector(
  featureRootSelector,
  ({ policy }) => policy && policy.can_manage
)
export const canViewHistorySelector = createSelector(
  featureRootSelector,
  ({ policy }) => policy && policy.can_view_history
)
export const policyAvailableCountriesSelector = createSelector(
  featureRootSelector,
  ({ policy }) => policy && policy.available_countries
)

export const policyStatesByCountrySelector = createSelector(
  featureRootSelector,
  ({ policy }) => policy && policy.states_by_country
)

const dealsPageDealSelector = ({ payment_link_form: paymentLinkForm }) => paymentLinkForm?.deal

export const dealsPageStatesByCountrySelector = createSelector(
  dealsPageDealSelector,
  (deal) => deal && deal.states_by_country
)

export const dealsPageAvailableCountriesSelector = createSelector(
  dealsPageDealSelector,
  (deal) => deal && deal.available_countries
)

const billingMaintenanceBannerSelector = createSelector(
  featureRootSelector,
  ({ billingMaintenanceBanner }) => billingMaintenanceBanner
)

export const taxInformationModalSelector = createSelector(
  featureRootSelector,
  ({ taxInformationModal }) => taxInformationModal
)

const churnModalsSelector = createSelector(
  featureRootSelector,
  ({ churnModals }) => churnModals
)

export const currentStageSelector = createSelector(
  currentSubscriptionSelector,
  ({ organization }) => organization && organization.stage
)
export const addonsSelector = createSelector(
  currentSubscriptionSelector,
  ({ addons }) => addons || []
)
export const hasAddonsSelector = createSelector(
  addonsSelector,
  (addons) => addons.length > 0
)
export const visibleAddonsSelector = createSelector(
  addonsSelector,
  (addons) => addons && addons.filter(
    (ao) => !(MEETING_INTELLIGENCE_ADDONS.includes(ao.sku_id) || MEETING_INSIGHTS_BETA_ADDONS.includes(ao.sku_id))
  )
)
export const miAddonSelector = createSelector(
  addonsSelector,
  (addons) => addons.find((ao) => MEETING_INTELLIGENCE_ADDONS.includes(ao.sku_id))
)
export const hasMIaddonSelector = createSelector(
  miAddonSelector,
  (miAddon) => !!miAddon
)
export const onlyHasMIAddonSelector = createSelector(
  addonsSelector,
  (addons) => addons.length > 0 && addons.every((ao) => MEETING_INTELLIGENCE_ADDONS.includes(ao.sku_id))
)
export const onlyHasXPBOrMIAddonSelector = createSelector(
  addonsSelector,
  (addons) => addons.length > 0
    && addons
      .every((ao) => {
        if (MEETING_INTELLIGENCE_ADDONS.includes(ao.sku_id)) return true
        return MEETING_INSIGHTS_BETA_ADDONS.includes(ao.sku_id)
      })
)
// DEPRECATED: prefer `isOnPaidStageSelector` for legibility
export const paidStageSelector = createSelector(
  currentStageSelector,
  (stage) => stage === 'paid'
)
export const pausedStageSelector = createSelector(
  currentStageSelector,
  (stage) => stage === 'paused'
)
export const isOnPaidStageSelector = paidStageSelector
export const isOnTrialStageSelector = createSelector(
  currentStageSelector,
  (stage) => stage === 'trial'
)
export const isOnFreeStageSelector = createSelector(
  currentStageSelector,
  (stage) => stage === 'free'
)
export const isFreeOrTrialSelector = createSelector(
  currentStageSelector,
  (stage) => stage === 'trial' || stage === 'free'
)
export const isVisibleSelector = createSelector(
  billingMaintenanceBannerSelector,
  ({ visible }) => visible
)
export const currentTierSelector = createSelector(
  currentSubscriptionSelector,
  ({ organization }) => organization && organization.tier
)
export const organizationNameSelector = createSelector(
  currentSubscriptionSelector,
  ({ organization }) => organization && organization.name
)
export const currentTierLowerSelector = createSelector(
  currentTierSelector,
  (tier) => tier?.toLowerCase()
)
export const unableToChangeTierSelector = createSelector(
  currentTierSelector,
  (tier) => ['enterprise', 'teams_plus'].includes(tier)
)
export const isEnterpriseSelector = createSelector(
  currentTierSelector,
  (tier) => tier === 'enterprise'
)
export const isBillingPageLoadingSelector = createSelector(
  currentSubscriptionSelector,
  ({ initialized }) => !initialized
)
const currentSubscriptionErrorSelector = createSelector(
  currentSubscriptionSelector,
  ({ error }) => error
)
export const billingPageLoadingErrorSelector = createSelector(
  billingPolicyErrorSelector,
  currentSubscriptionErrorSelector,
  (policyError, subscriptionError) => policyError || subscriptionError
)
export const trialEndsAtSelector = createSelector(
  currentSubscriptionSelector,
  ({ trial }) => trial && trial.ends_at
)
export const trialEndedAtSelector = createSelector(
  currentSubscriptionSelector,
  ({ trial }) => trial && trial.ended_at
)

export const trialEndsInDaysSelector = createSelector(
  currentSubscriptionSelector,
  ({ trial }) => trial && trial.ends_in_days
)
export const trialStartedAtSelector = createSelector(
  currentSubscriptionSelector,
  ({ trial }) => trial && trial.created_at
)
export const isTrialActiveSelector = createSelector(
  currentSubscriptionSelector,
  ({ trial }) => trial && !trial.ended_at
)

export const trialTypeSelector = createSelector(
  currentSubscriptionSelector,
  ({ trial }) => trial && trial.trial_type
)
export const isMiTrialSelector = createSelector(
  trialTypeSelector,
  (trialType) => trialType === 'calendly_all'
)
export const isSubscriptionCanceledSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => !!subscription && subscription.status === 'canceled'
)
export const isSubscriptionExpiringSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => !!subscription && subscription.status === 'expiring'
)
export const isSubscriptionPausedSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => !!subscription && subscription.status === 'paused'
)
export const isSubscriptionPaymentPendingSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => !!subscription && subscription.status === 'payment_pending'
)
export const subscriptionEndedAtSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => (
    subscription && (subscription.ended_at || subscription.canceled_at)
  )
)
export const subscriptionEndedAtDateSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.ended_at
)
export const subscriptionProviderSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.provider
)

export const isSubscriptionLockedSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.is_currently_locked
)

export const invoiceViewableSelector = ({ paid, invoiceRecord }) => createSelector(
  subscriptionProviderSelector,
  (provider) => {
    if (!provider) return false
    if (provider === 'stripe') return paid && invoiceRecord?.download_url
    return paid && invoiceRecord
  }
)

export const subscriptionPlanSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.external_plan_id
)
export const subscriptionPlanWithMISelector = createSelector(
  currentSubscriptionSelector,
  hasMIaddonSelector,
  ({ subscription }, hasMi) => {
    if (!subscription) return null

    const planId = subscription.external_plan_id

    if (hasMi) return `calendly_all_${planId}`

    return planId
  }
)
const subscriptionPlanVariantSuffixSelector = createSelector(
  subscriptionPlanSelector,
  (planId) => planId && (planId?.match(/-var[12]$/)?.[0] || null)
)

const planNameAndFrequencySelector = createSelector(
  currentTierSelector,
  subscriptionPlanSelector,
  (currentTier, planId) => {
    if (!planId) return { planName: currentTier, planFrequency: null }

    const [planName, planFrequency] = standardizeSku(planId).split('_', 2)
    return { planName, planFrequency }
  }
)
export const planSelector = createSelector(
  planNameAndFrequencySelector,
  ({ planName, _planFrequency }) => planName
)
export const planSelectorWithMI = createSelector(
  planSelector,
  hasMIaddonSelector,
  (plan, hasMIaddon) => (hasMIaddon ? `${plan}_all` : plan)
)
export const frequencySelector = createSelector(
  planNameAndFrequencySelector,
  ({ _planName, planFrequency }) => planFrequency
)
export const isAvailableAddonsLoadingSelector = createSelector(
  availableAddonsSelector,
  ({ initialized }) => !initialized
)
const availableAddonsContentSelector = createSelector(
  availableAddonsSelector,
  ({ content: availableAddons }) => availableAddons || []
)

const currentSubscriptionCurrencySelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.currency_code
)
export const purchasableAddonsSelector = createSelector(
  unableToChangeTierSelector,
  addonsSelector,
  availableAddonsContentSelector,
  frequencySelector,
  currentSubscriptionCurrencySelector,
  subscriptionPlanVariantSuffixSelector,
  (unableToChangeTier, addons, availableAddons, planFrequency, subscriptionCurrencyCode, planVariantSuffix) => {
    if (unableToChangeTier) return []

    const isPurchased = (addon) => addons.some(
      (purchasedAddon) => purchasedAddon.sku_id === addon.sku_id
    )

    return availableAddons.filter((addon) => {
      if (isPurchased(addon)) return false

      const currencyMatches = addon.price.currency_unit === subscriptionCurrencyCode
      const frequencyMatches = addon.billing_period === planFrequency
      const selfServe = addon.presentation === 'self_serve'
      const variantMatches = planVariantSuffix ? addon.sku_id.endsWith(planVariantSuffix) : true

      return currencyMatches && frequencyMatches && selfServe && variantMatches
    })
  }
)
export const activeSubscriptionBillingPeriodSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => {
    if (subscription && subscription.status !== 'canceled') {
      return subscription.billing_period
    }
    return null
  }
)
export const subscriptionQuantitySelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.external_quantity
)
const tierMaximumSeatCountSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.tier_maximum_seat_count
)
export const unableToAddSeatsSelector = createSelector(
  subscriptionQuantitySelector,
  tierMaximumSeatCountSelector,
  (quantity, maximum) => quantity && maximum && (maximum <= quantity)
)
export const externalCustomerSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.external_customer_id
)
export const externalSubscriptionIdSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.external_subscription_id
)
export const subUUIDSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.uuid
)
export const paymentMethodSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.payment_method
)
export const isPaymentMethodCCSelector = createSelector(
  paymentMethodSelector,
  (paymentMethod) => paymentMethod === 'credit_card'
)
export const isPaymentMethodPaypalSelector = createSelector(
  paymentMethodSelector,
  (paymentMethod) => paymentMethod === 'paypal'
)
export const currentPeriodEndSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.current_period_end
)
export const currentSubscriptionCardSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.card
)
export const subscriptionActiveSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.status !== 'canceled'
)
export const activeSubscriptionCurrencySelector = createSelector(
  subscriptionActiveSelector,
  currentSubscriptionCurrencySelector,
  (active, currency) => (active ? currency : null)
)
export const isManualSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.status !== 'canceled' && subscription.is_manual
)
export const isMonthlySelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.is_monthly
)
export const minQuantitySelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.min_quantity
)
export const monthlyToYearlySavingsSelector = createSelector(
  currentSubscriptionSelector,
  ({ subscription }) => subscription && subscription.monthly_to_yearly_savings
)
export const paymentPendingSelector = createSelector(
  paymentSelector,
  ({ status }) => status === 'pending'
)
export const paymentAttemptErrorSelector = createSelector(
  paymentSelector,
  ({ error }) => error
)
export const paymentAttemptCardErrorSelector = createSelector(
  paymentSelector,
  ({ error, isCardError }) => isCardError && error
)
export const paymentSuccessfulSelector = createSelector(
  paymentSelector,
  ({ status, error }) => status === 'completed' && !error
)
export const upgradeInfoSelector = createSelector(
  billingInfoSelector,
  ({ upgradeInfo }) => upgradeInfo
)
export const upgradeInfoLoadingSelector = createSelector(
  billingInfoSelector,
  ({ upgradeInfoLoading }) => upgradeInfoLoading
)
export const upgradeSubscriptionLoadingSelector = createSelector(
  upgradeSubscriptionSelector,
  ({ loading }) => loading
)
export const upgradeSubscriptionSuccessfulSelector = createSelector(
  upgradeSubscriptionSelector,
  ({ upgradeSuccessful }) => upgradeSuccessful
)
export const upgradeSubscriptionCardErrorSelector = createSelector(
  upgradeSubscriptionSelector,
  ({ cardError }) => cardError
)
export const contactSupportTierSelector = createSelector(
  contactSupportSelector,
  ({ tier }) => tier
)
export const showContactSupportModalSelector = createSelector(
  contactSupportSelector,
  ({ showModal }) => showModal
)

export const nextBillSelector = createSelector(
  featureRootSelector,
  ({ nextBill }) => nextBill
)
export const nextQuantitySelector = createSelector(
  nextBillSelector,
  ({ plan_quantity: nextQuantity }) => nextQuantity
)
export const nextTierSelector = createSelector(
  nextBillSelector,
  ({ plan_tier: nextTier }) => nextTier
)
export const nextPlanSelectorWithMI = createSelector(
  nextBillSelector,
  nextTierSelector,
  ({ plan_id: planId }, nextTier) => {
    if (planId?.startsWith('calendly_all')) {
      return `${nextTier}_all`
    }

    return nextTier || 'basic'
  }
)
export const nextPlanIdSelector = createSelector(
  nextBillSelector,
  ({ plan_id: planId }) => {
    // Handle case where plan_id might be a plan object instead of a string
    if (planId == null) {
      return null
    }
    if (typeof planId === 'object') {
      // Use 'in' operator to check for id property more reliably
      if ('id' in planId && planId.id != null) {
        return planId.id
      }
      // If object doesn't have id, return null to indicate error
      console.error('nextPlanIdSelector: plan object missing id property', planId)
      return null
    }
    // If it's already a string, return it
    if (typeof planId === 'string') {
      return planId
    }
    // Fallback: try to convert to string if possible
    console.error('nextPlanIdSelector: unexpected planId type', { planId, type: typeof planId })
    return null
  }
)

export const scheduledPauseDateSelector = createSelector(
  nextBillSelector,
  ({ scheduled_pause_date: scheduledPauseDate }) => scheduledPauseDate
)

export const isPauseScheduledSelector = createSelector(
  scheduledPauseDateSelector,
  currentSubscriptionSelector,
  (pauseDate, { subscription }) => pauseDate && subscription.status === 'active'
)

const isQuantityRemovalScheduledSelector = createSelector(
  subscriptionProviderSelector,
  nextQuantitySelector,
  subscriptionQuantitySelector,
  (provider, nextQuantity, currentQuantity) => {
    if (provider !== 'stripe') return false
    if (nextQuantity) return nextQuantity < currentQuantity
    return false
  }
)

const isTierDowngradeScheduledSelector = createSelector(
  nextPlanSelectorWithMI,
  nextTierSelector,
  currentTierSelector,
  hasMIaddonSelector,
  (nextPlanWithMI, nextTier, currentTier, hasMIaddon) => {
    // i.e,
    //   Standard+ to Standard
    //   Teams+ to Teams
    if (currentTier === nextTier) {
      const currentPlanIsMi = hasMIaddon
      const newPlanIsMi = nextPlanWithMI.includes('_all')

      if (currentPlanIsMi && !newPlanIsMi) return true
    }

    return nextTier && paidSkus.indexOf(nextTier) < paidSkus.indexOf(currentTier)
  }
)

export const isDowngradeScheduledSelector = createSelector(
  isQuantityRemovalScheduledSelector,
  isTierDowngradeScheduledSelector,
  (quantityRemoval, tierDowngrade) => quantityRemoval || tierDowngrade
)

export const scheduledChangeTypeSelector = createSelector(
  isQuantityRemovalScheduledSelector,
  isTierDowngradeScheduledSelector,
  (quantityRemoval, tierDowngrade) => {
    if (quantityRemoval) return 'quantity'
    if (tierDowngrade) return 'plan'
    return null
  }
)

export const billingDateSelector = createSelector(
  nextBillSelector,
  ({ billing_date: billingDate }) => billingDate
)

export const cancellationSurveyDataSelector = createSelector(
  downgradeSelector,
  (downgradeData) => ({
    pageOneData: downgradeData?.pageOneData,
    pageTwoData: downgradeData?.pageTwoData
  })
)

export const cancellationReasonIsOtherSelector = createSelector(
  cancellationSurveyDataSelector,
  ({ pageOneData }) => pageOneData?.reason === 'other'
)

export const hasPricingSensitivityChangeSelector = createSelector(
  currentSubscriptionSelector,
  ({ is_in_pricing_test: hasPricingChange }) => hasPricingChange
)

const pricingSensitivityUnitPricesSelector = createSelector(
  currentSubscriptionSelector,
  ({ before_and_after_unit_prices: beforeAndAfterUnitPrices }) => beforeAndAfterUnitPrices
)

export const hadCalendlyAllTrialSelector = createSelector(
  currentSubscriptionSelector,
  ({ had_calendly_all_trial: hadCalendlyAllTrial }) => hadCalendlyAllTrial
)

export const beforeAndAfterUnitPricesSelector = createSelector(
  pricingSensitivityUnitPricesSelector,
  ({ before: beforePrice, after: afterPrice }) => ({ beforePrice, afterPrice })
)

export const isAvailableFeatureSelector = ({ billing }, { featureKey }) => {
  if (!featureKey) return false

  const { userFeatures } = billing
  if (userFeatures) {
    return userFeatures.features.includes(featureKey)
  }
  throw new Error('isAvailableFeatureSelector: Cannot find user features')
}

export const isMIFeatureAvailableSelector = (state) => isAvailableFeatureSelector(
  state, { featureKey: 'meeting_intelligence' }
)

export const isRestrictedFeatureSelector = createSelector(
  isAvailableFeatureSelector,
  (isAvailableFeature) => !isAvailableFeature
)

const trialFeatureOverride = (featureKey) => restrictedOnTrialFeatures.includes(featureKey)

export const isRestrictedOnTrialFeatureSelector = (state, { featureKey }) => {
  const { billing } = state
  const { currentSubscription } = billing
  const stage = currentSubscription?.organization?.stage
  if (!stage) return false

  return trialFeatureOverride(featureKey) && isOnTrialStageSelector(state)
}

export const rawFeatureTierSelector = ({ billing }, { featureKey }) => {
  if (!featureKey) return null

  const { featureInfo } = billing
  if (featureInfo) {
    return featureInfo[featureKey]?.minimum_plan
  }
  return null
}

export const featureTierSelector = ({ billing }, { featureKey }) => {
  if (!featureKey) return null

  const { featureInfo } = billing
  if (featureInfo) {
    const feature = featureInfo[featureKey]
    if (!feature) {
      return null
    }

    return humanizeTier(feature.minimum_plan)
  }
  throw new Error('featureTierSelector: Cannot find feature info')
}

export const isLegacyPlanSelector = createSelector(
  currentTierLowerSelector,
  (tier) => legacySkus.includes(tier)
)

export const isTaxInformationModalVisibleSelector = createSelector(
  taxInformationModalSelector,
  ({ isVisible }) => isVisible
)

export const isTaxInformationConfirmationModalVisibleSelector = createSelector(
  taxInformationModalSelector,
  ({ isConfirmationVisible }) => isConfirmationVisible
)

export const isTaxInformationSuccessModalVisibleSelector = createSelector(
  taxInformationModalSelector,
  ({ isSuccessVisible }) => isSuccessVisible
)

export const isTaxInformationDeleteModalVisibleSelector = createSelector(
  taxInformationModalSelector,
  ({ isDeleteVisible }) => isDeleteVisible
)

export const isTaxInformationCannotValidateModalVisibleSelector = createSelector(
  taxInformationModalSelector,
  ({ isCannotValidatePopupVisible }) => isCannotValidatePopupVisible
)

export const taxInformationModalValidatedFormDataSelector = createSelector(
  taxInformationModalSelector,
  ({ validatedFormData }) => validatedFormData
)

export const taxIdValidationCallSelector = createSelector(
  taxInformationModalSelector,
  ({ validationCall }) => validationCall
)

export const taxInformationModalInvalidFormFieldsSelector = createSelector(
  taxInformationModalSelector,
  ({ invalidFormFields }) => invalidFormFields
)

export const taxInformationModalShouldUpdateExistingSubscriptionSelector = createSelector(
  taxInformationModalSelector,
  ({ shouldUpdateExistingSubscription }) => shouldUpdateExistingSubscription
)

export const taxInformationModalConfirmedTaxInformationSelector = createSelector(
  taxInformationModalSelector,
  ({ confirmedTaxInformation }) => confirmedTaxInformation
)

export const taxInformationModalPopupLocation = createSelector(
  taxInformationModalSelector,
  ({ popupLocation }) => popupLocation
)

export const taxInformationModelShowThankYouPopup = createSelector(
  taxInformationModalSelector,
  ({ showThankYouPopup }) => showThankYouPopup
)

export const isPersonalizedChurnModalOpenSelector = createSelector(
  churnModalsSelector,
  ({ isPersonalizedChurnModalOpen }) => isPersonalizedChurnModalOpen
)

export const isEtSelectionModalOpenSelector = createSelector(
  churnModalsSelector,
  ({ isEtSelectionModalOpen }) => isEtSelectionModalOpen
)
